import { useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Outlet, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  Section,
  SectionLink,
  SectionItem,
  SectionName,
} from '../components/Chrome';
import {
  Drawer,
  DrawerExit,
  DrawerLink,
  DrawerList,
  DrawerTrigger,
} from '../components/Drawer';
import Loader from '../components/Loader';
import { A } from '../components/Markdown';
import Icon from '../components/Icon';
import DeleteTagModal from '../modals/DeleteTag';
import EditTagModal from '../modals/EditTag';
import { SPACE, SPACE_TAGS } from '../utilities/queries';
import { useSpaceContext } from '../contexts/space';

const SpaceSettingsTagsPage = () => {
  const params = useParams();
  const { spaceVars } = useSpaceContext();
  const { data: spaceData, loading: spaceLoading } = useQuery(SPACE, {
    variables: { slug: params.s, ...spaceVars },
  });
  const { data: spaceTagsData, loading: spaceTagsLoading } = useQuery(
    SPACE_TAGS,
    {
      variables: { slug: params.s },
    }
  );

  const [tagId, setTagId] = useState();
  const [tagName, setTagName] = useState();
  const [isDeleteTagModalOpen, setIsDeleteTagModalOpen] = useState(false);
  const [isEditTagModalOpen, setIsEditTagModalOpen] = useState(false);

  // Tracks the tag the viewer is interacting with
  const listRef = useRef();
  const [trackTag, setTrackTag] = useState();

  return (
    <>
      <Helmet>
        <title>Tags</title>
      </Helmet>
      <Section>
        {spaceLoading || spaceTagsLoading ? (
          <Loader />
        ) : (
          <>
            <SectionName isSecondLevel>Tags</SectionName>
            {spaceData &&
            spaceTagsData &&
            spaceData.space.planLevel === 0 &&
            spaceTagsData.space.tags.length >= 3 ? (
              <SectionItem>
                <span>
                  Max tags for your{' '}
                  <A href={`/s/${params.s}/settings/plan`}>plan</A>.
                </span>
              </SectionItem>
            ) : (
              <SectionLink to="new">
                New tag <Icon name="arrow-right" />
              </SectionLink>
            )}
            <SectionItem isBlock />
            {spaceTagsData?.space?.tags && (
              <>
                {spaceTagsData.space.tags.map((tag) => (
                  <Drawer key={tag.id} onClose={() => setTrackTag(null)}>
                    <DrawerTrigger onClick={() => setTrackTag(tag.id)}>
                      <SectionLink as="button" isActive={trackTag === tag.id}>
                        {tag.name}
                        <Icon name="more" />
                      </SectionLink>
                    </DrawerTrigger>
                    <DrawerList ref={listRef}>
                      <DrawerExit onClick={() => setTrackTag(null)} />
                      <DrawerLink
                        as="button"
                        onClick={() => {
                          setTagId(tag.id);
                          setTagName(tag.name);
                          setIsEditTagModalOpen(true);
                        }}
                        dontRunOnClose
                      >
                        Edit tag
                      </DrawerLink>
                      <DrawerLink
                        as="button"
                        onClick={() => {
                          setTagId(tag.id);
                          setTagName(tag.name);
                          setIsDeleteTagModalOpen(true);
                        }}
                        dontRunOnClose
                      >
                        Delete tag
                      </DrawerLink>
                    </DrawerList>
                  </Drawer>
                ))}
              </>
            )}
            {spaceTagsData.space.tags.length == 0 && (
              <SectionItem>There are no tags in this space — yet.</SectionItem>
            )}
          </>
        )}
      </Section>
      {isDeleteTagModalOpen && (
        <DeleteTagModal
          tagId={tagId}
          tagName={tagName}
          onClose={() => {
            setTrackTag(null);
            setIsDeleteTagModalOpen(false);
          }}
        />
      )}
      {isEditTagModalOpen && (
        <EditTagModal
          tagId={tagId}
          tagName={tagName}
          onClose={() => {
            setTrackTag(null);
            setIsEditTagModalOpen(false);
          }}
        />
      )}
      {
        // prettier-ignore
        spaceData &&
        spaceTagsData &&
        ((spaceData.space.planLevel === 0 &&
        spaceTagsData.space.tags.length < 3) ||
      (spaceData.space.planLevel === 1 &&
        spaceTagsData.space.tags.length < 30) ||
      spaceData.space.planLevel === 2) ? (
        <Outlet />
      ) : null
      }
    </>
  );
};

export default SpaceSettingsTagsPage;
